import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, FileText, Inbox, Paperclip, ShieldCheck, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { canManageRfqScopes } from "@/features/rfq/api/scopes";
import {
  EXTENSION_STATUS_COLOR,
  useDecideScopeRequest,
  useScopeRequests,
  type ExtensionStatus,
  type ScopeExtensionRequest,
} from "@/features/rfq/api/requests";

/**
 * Wnioski podwykonawców o rozszerzenie zakresu — kolejka dyrektora technicznego.
 *
 * Zatwierdzenie od razu nadaje kategorie i województwa, więc decyzja jest tu
 * ostateczna i nie wymaga drugiego kliknięcia na karcie firmy.
 */

type Decision = Exclude<ExtensionStatus, "pending">;

const DECISIONS: { value: Decision; label: string; hint: string }[] = [
  {
    value: "approved",
    label: "Zatwierdź",
    hint: "Firma dostaje pełną kwalifikację we wnioskowanych kategoriach.",
  },
  {
    value: "approved_conditional",
    label: "Zatwierdź warunkowo",
    hint: "Kwalifikacja warunkowa — ostrzeżenie przy trzecim zleceniu, pełna po POCK bez uwag.",
  },
  {
    value: "rejected",
    label: "Odrzuć",
    hint: "Powód trafia do podwykonawcy, więc napisz, czego brakuje.",
  },
];

export function ScopeRequestsPage() {
  const { user } = useAuthStore();
  const canDecide = canManageRfqScopes(user);
  usePageTitle("Wnioski o rozszerzenie zakresu");

  const [onlyPending, setOnlyPending] = useState(true);
  const { data: requests, isLoading } = useScopeRequests(onlyPending ? "pending" : undefined);
  const decide = useDecideScopeRequest();

  const [deciding, setDeciding] = useState<ScopeExtensionRequest | null>(null);
  const [decision, setDecision] = useState<Decision>("approved");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  if (isLoading) return <PageLoader />;
  const list = requests ?? [];

  const submitDecision = () => {
    if (!deciding) return;
    if (decision === "rejected" && !note.trim()) {
      setError("Przy odrzuceniu podaj powód — trafia do podwykonawcy.");
      return;
    }
    setError("");
    decide.mutate(
      { id: deciding.id, decision, note: note.trim() },
      {
        onSuccess: () => {
          setDeciding(null);
          setNote("");
          setDecision("approved");
        },
        onError: () => setError("Nie udało się zapisać decyzji."),
      },
    );
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Wnioski o rozszerzenie zakresu
        </h1>
        <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          Podwykonawcy proszą o dostęp do kolejnych kategorii prac i województw.
          Zatwierdzenie nadaje uprawnienia od razu — nie trzeba ich dopisywać ręcznie
          na karcie firmy.
        </p>
      </div>

      <label className="mb-4 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
        <input
          type="checkbox"
          checked={onlyPending}
          onChange={(e) => setOnlyPending(e.target.checked)}
        />
        Tylko nierozpatrzone
      </label>

      {list.length === 0 ? (
        <div className="border border-dashed border-[var(--color-border)] px-6 py-12 text-center">
          <Inbox size={28} className="mx-auto mb-3 text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            {onlyPending ? "Brak wniosków do rozpatrzenia." : "Brak wniosków."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((request) => (
            <article
              key={request.id}
              className="border-l-4 border-[var(--color-primary)] bg-white p-4 dark:bg-gray-800"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/contractors/${request.contractor}`}
                  className="font-semibold text-[var(--color-primary)] hover:underline"
                >
                  {request.contractor_name}
                </Link>
                <Badge color={EXTENSION_STATUS_COLOR[request.status]}>
                  {request.status_label}
                </Badge>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {new Date(request.created_at).toLocaleDateString("pl")}
                  {request.submitted_by_name && ` · ${request.submitted_by_name}`}
                </span>
              </div>

              <p className="mt-2 text-sm text-[var(--color-text-primary)]">
                {[
                  ...request.categories.map((c) => c.name),
                  ...request.positions.map((p) => `${p.category_name}: ${p.name}`),
                  ...request.voivodeship_labels,
                ].join(" · ") || "—"}
              </p>

              <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
                {request.description}
              </p>

              {request.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-3">
                  {request.attachments.map((attachment) => (
                    <ManagedFileLink
                      key={attachment.id}
                      file={attachment}
                      icon={<Paperclip size={12} />}
                    >
                      {attachment.original_filename}
                    </ManagedFileLink>
                  ))}
                </div>
              )}

              {request.decision_note && (
                <p className="mt-2 border-l-2 border-[var(--color-border)] pl-2 text-xs text-[var(--color-text-secondary)]">
                  {request.decided_by_name}: {request.decision_note}
                </p>
              )}

              {request.status === "pending" && canDecide && (
                <div className="mt-3">
                  <Button variant="secondary" onClick={() => setDeciding(request)}>
                    <ShieldCheck size={14} className="mr-1.5 inline" /> Rozpatrz
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {deciding && (
        <Modal open onClose={() => setDeciding(null)} title={`Wniosek: ${deciding.contractor_name}`}>
          <div className="space-y-4">
            <div className="bg-[var(--color-bg-elevated)] p-3 text-sm">
              <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                <FileText size={12} /> Wnioskowany zakres
              </p>
              <p className="mt-1 text-[var(--color-text-primary)]">
                {[
                  ...deciding.categories.map((c) => c.name),
                  ...deciding.positions.map((p) => `${p.category_name}: ${p.name}`),
                  ...deciding.voivodeship_labels,
                ].join(" · ")}
              </p>
            </div>

            <div className="space-y-2">
              {DECISIONS.map((option) => (
                <label key={option.value} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="decision"
                    className="mt-1"
                    checked={decision === option.value}
                    onChange={() => setDecision(option.value)}
                  />
                  <span>
                    <span className="text-[var(--color-text-primary)]">{option.label}</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      {option.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div>
              <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Uzasadnienie {decision === "rejected" && "(wymagane)"}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                placeholder="Trafia do podwykonawcy."
              />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeciding(null)}>
                <X size={14} className="mr-1.5 inline" /> Anuluj
              </Button>
              <Button onClick={submitDecision} disabled={decide.isPending}>
                <Check size={14} className="mr-1.5 inline" />
                {decide.isPending ? "Zapisywanie…" : "Zapisz decyzję"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
